import { useRef, useState } from "react";
import ThumbsUpIcon from "@sprout/icons/thumbs_up";
import ThumbsDownIcon from "@sprout/icons/thumbs_down";
import Tooltip from "@sprout/tooltip.tsx";
import useTranslation from "@/hooks/use_translation";
import DislikeReasonPopover, { type DislikeReason } from "./dislike_reason_popover.tsx";

export type RecommendationFeedback = "like" | "dislike";

type Props = {
  feedback: RecommendationFeedback | null;
  onChange: (next: RecommendationFeedback | null, reason?: DislikeReason) => void;
  collectDislikeReason?: boolean;
};

const buttonClass = (selected: boolean) =>
  [
    "rounded-full p-1! transition-colors disabled:opacity-50",
    "active:bg-[var(--pale-blue)]!",
    selected ? "bg-[var(--green-100)]!" : "hover:bg-[var(--green-50)]!",
  ].join(" ");

const RecommendationFeedbackButtons = ({
  feedback,
  onChange,
  collectDislikeReason = false,
}: Props) => {
  const { t } = useTranslation("jobSearch");
  const [reasonOpen, setReasonOpen] = useState(false);
  const dislikeRef = useRef<HTMLButtonElement>(null);

  const stop = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const toggleLike = (event: React.MouseEvent) => {
    stop(event);

    if (reasonOpen) {
      setReasonOpen(false);
      onChange("like");
      return;
    }

    onChange(feedback === "like" ? null : "like");
  };

  const toggleDislike = (event: React.MouseEvent) => {
    stop(event);

    if (!collectDislikeReason) {
      onChange(feedback === "dislike" ? null : "dislike");
      return;
    }

    if (reasonOpen) {
      setReasonOpen(false);
      onChange(null);
      return;
    }

    if (feedback === "dislike") {
      onChange(null);
      return;
    }

    setReasonOpen(true);
  };

  const dismissReason = () => {
    setReasonOpen(false);
    onChange("dislike");
  };

  const submitReason = (reason: DislikeReason) => {
    setReasonOpen(false);
    onChange("dislike", reason);
  };

  const dislikeSelected = feedback === "dislike" || reasonOpen;
  const likeSelected = feedback === "like" && !reasonOpen;

  const buttons = (
    <div className="flex gap-1 items-center flex-none">
      <Tooltip text={t("recommendationFeedback.like")} asChild>
        <button
          type="button"
          className={buttonClass(likeSelected)}
          aria-label={t("recommendationFeedback.like")}
          aria-pressed={likeSelected}
          onClick={toggleLike}
        >
          <ThumbsUpIcon size="md" color="dark-green" />
        </button>
      </Tooltip>
      <Tooltip text={t("recommendationFeedback.dislike")} asChild>
        <button
          type="button"
          ref={dislikeRef}
          className={buttonClass(dislikeSelected)}
          aria-label={t("recommendationFeedback.dislike")}
          aria-pressed={dislikeSelected}
          onClick={toggleDislike}
        >
          <ThumbsDownIcon size="md" color="dark-green" />
        </button>
      </Tooltip>
    </div>
  );

  if (!collectDislikeReason) return buttons;

  return (
    <DislikeReasonPopover
      open={reasonOpen}
      onDismiss={dismissReason}
      onSubmit={submitReason}
      triggerRef={dislikeRef}
    >
      {buttons}
    </DislikeReasonPopover>
  );
};

export default RecommendationFeedbackButtons;
